/**
 * <bike-finder> — carte « finder » de la bannière homepage.
 * - Dropdown « Type de vélo » custom (options chargées depuis la collection).
 * - Compteur de vélos en direct + détection « aucun résultat ».
 * - Toggle de motorisation animé (pastille coulissante via data-active).
 * - Snapping de la taille sur les valeurs de filtre réelles.
 * - Redirige vers la collection filtrée à la soumission.
 *
 * Données (compteur, valeurs de filtres) récupérées via le template léger
 * `templates/collection.finder.liquid` (?view=finder → JSON), au lieu de parser le HTML.
 */
if (!customElements.get('bike-finder')) {
  const FINDER_VIEW = 'finder';

  class BikeFinder extends HTMLElement {
    constructor() {
      super();
      this.form = this.querySelector('[data-finder-form]');
      this.submitButton = this.querySelector('[data-finder-submit]');
      this.countEl = this.querySelector('[data-finder-count]');
      this.motorToggle = this.querySelector('[data-motor-toggle]');
      this.sizeInput = this.querySelector('[data-finder-size]');
      this.noResults = this.querySelector('[data-finder-no-results]');
      this.collectionUrl = this.dataset.collectionUrl || '/collections/all';
      this.showCount = this.dataset.showCount === 'true' && !!this.countEl;
      this.excludeValues = (this.dataset.excludeValues || '')
        .split(',')
        .map((s) => s.trim().toLowerCase())
        .filter(Boolean);

      // Dropdown custom.
      this.dropdown = this.querySelector('[data-finder-category]');
      this.dropdownTrigger = this.querySelector('[data-dropdown-trigger]');
      this.dropdownList = this.querySelector('[data-dropdown-list]');
      this.dropdownLabel = this.querySelector('[data-dropdown-label]');
      this.dropdownInput = this.querySelector('[data-dropdown-input]');
      this.allLabel = this.dropdownLabel ? this.dropdownLabel.textContent.trim() : '';

      this.debounceTimer = null;
      this.countAbort = null;
      this.dataAbort = null;
      this.cache = new Map();
      this.sizeValues = null; // valeurs de filtre taille réelles (pour le snapping)

      this.onDocumentClick = this.onDocumentClick.bind(this);
    }

    connectedCallback() {
      if (!this.form) return;

      this.form.addEventListener('submit', this.handleSubmit.bind(this));

      if (this.motorToggle) {
        this.motorToggle.addEventListener('change', this.syncToggle.bind(this));
      }

      if (this.dropdown) this.setupDropdown();

      if (this.sizeInput) {
        // Snap sur la valeur de filtre existante la plus proche à la validation du champ.
        this.sizeInput.addEventListener('change', () => this.snapSizeInput());
      }

      // Un seul fetch JSON pour les options catégorie + les valeurs de taille.
      if (this.dropdown || this.sizeInput) this.loadFilterData();

      if (this.showCount) {
        this.form.addEventListener('input', this.onChange.bind(this));
        this.form.addEventListener('change', this.onChange.bind(this));
        this.updateCount();
      }

      if (this.noResults) {
        // Masque le message « aucun résultat » dès que l'utilisateur ajuste ses filtres.
        this.form.addEventListener('input', () => this.hideNoResults());
        this.form.addEventListener('change', () => this.hideNoResults());
      }
    }

    disconnectedCallback() {
      document.removeEventListener('click', this.onDocumentClick);
    }

    /* ------------------------------------------------------------------ *
     * Endpoint JSON (?view=finder)
     * ------------------------------------------------------------------ */

    finderJsonUrl(query) {
      const sep = this.collectionUrl.indexOf('?') === -1 ? '?' : '&';
      const parts = [];
      if (query) parts.push(query);
      parts.push('view=' + FINDER_VIEW);
      return this.collectionUrl + sep + parts.join('&');
    }

    async fetchFinderData(query, signal) {
      const response = await fetch(this.finderJsonUrl(query), {
        signal,
        headers: { 'X-Requested-With': 'XMLHttpRequest' },
      });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      return response.json();
    }

    /** Extrait le nombre de résultats du JSON (string ou null si indéterminé). */
    readCount(data) {
      return data && data.products_count != null ? String(data.products_count) : null;
    }

    /* ------------------------------------------------------------------ *
     * Query + soumission
     * ------------------------------------------------------------------ */

    buildQuery() {
      const params = new URLSearchParams();
      this.form.querySelectorAll('[data-finder-input]').forEach((input) => {
        if ((input.type === 'radio' || input.type === 'checkbox') && !input.checked) return;
        const name = input.getAttribute('name');
        let value = (input.value || '').trim();
        if (!name || value === '') return;
        if (input === this.sizeInput) {
          // Liste de valeurs discrètes : Shopify ignore une valeur absente.
          // On snappe sur la valeur existante la plus proche (sans muter le champ).
          const snapped = this.snappedSize(value);
          if (snapped === null) return;
          value = String(snapped);
        }
        params.append(name, value);
      });
      return params.toString();
    }

    targetUrl() {
      const query = this.buildQuery();
      return query ? `${this.collectionUrl}?${query}` : this.collectionUrl;
    }

    async handleSubmit(event) {
      event.preventDefault();
      this.snapSizeInput();

      const query = this.buildQuery();
      const url = query ? `${this.collectionUrl}?${query}` : this.collectionUrl;

      // Résout le nombre de résultats : cache si dispo, sinon fetch JSON léger.
      let count = this.cache.has(query) ? this.cache.get(query) : null;
      if (count === null) {
        this.setBusy(true);
        try {
          count = this.readCount(await this.fetchFinderData(query));
          if (count !== null) this.cache.set(query, count);
        } catch (error) {
          // Indéterminé → fail-open (on redirige, on ne bloque jamais l'utilisateur à tort).
        } finally {
          this.setBusy(false);
        }
      }

      // 0 résultat certain (et bloc message présent) → message + alerte ;
      // sinon (résultats, count inconnu, ou bloc message absent) → redirection.
      if (count !== null && parseInt(count, 10) === 0 && this.noResults) {
        this.showNoResults();
      } else {
        window.location.href = url;
      }
    }

    showNoResults() {
      if (!this.noResults) return;
      this.noResults.removeAttribute('hidden');
      // Déplace le focus sur l'action (bouton d'alerte) pour les utilisateurs clavier ;
      // le message est aussi annoncé via aria-live (role="status").
      const alertBtn = this.noResults.querySelector('.hpb-finder__alert-btn');
      if (alertBtn) alertBtn.focus();
    }

    hideNoResults() {
      if (this.noResults) this.noResults.setAttribute('hidden', '');
    }

    /* ------------------------------------------------------------------ *
     * Toggle motorisation
     * ------------------------------------------------------------------ */

    syncToggle() {
      const checked = this.motorToggle.querySelector('input[data-motor]:checked');
      if (checked && checked.dataset.motor) {
        this.motorToggle.dataset.active = checked.dataset.motor;
      }
    }

    /* ------------------------------------------------------------------ *
     * Dropdown custom
     * ------------------------------------------------------------------ */

    setupDropdown() {
      this.dropdownTrigger.addEventListener('click', () => this.toggleDropdown());
      this.dropdownTrigger.addEventListener('keydown', (e) => {
        if (e.key === 'ArrowDown' || e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          this.toggleDropdown(true);
          this.focusOption(0);
        }
      });
      this.dropdownList.addEventListener('click', (e) => {
        const option = e.target.closest('[role="option"]');
        if (option) this.selectOption(option);
      });
      this.dropdownList.addEventListener('keydown', (e) => this.onListKeydown(e));
    }

    toggleDropdown(force) {
      const open = typeof force === 'boolean' ? force : !this.dropdown.classList.contains('is-open');
      if (open) this.positionDropdown();
      this.dropdown.classList.toggle('is-open', open);
      this.dropdownTrigger.setAttribute('aria-expanded', open ? 'true' : 'false');
      if (open) {
        document.addEventListener('click', this.onDocumentClick);
      } else {
        document.removeEventListener('click', this.onDocumentClick);
      }
    }

    /**
     * Hauteur occupée en haut du viewport par tout élément épinglé (sticky/fixe) :
     * announcement bar, header, barre de CTA… Cet espace n'est pas utilisable par la
     * dropdown (elle passerait derrière). Détecté génériquement en sondant une bande
     * verticale près du haut : on retient la base la plus basse d'un élément épinglé.
     * Renvoie 0 si rien n'est épinglé (ex. header non sticky).
     */
    topInset() {
      const x = Math.min(Math.max(window.innerWidth / 2, 1), window.innerWidth - 1);
      const maxProbe = window.innerHeight * 0.6; // garde-fou : ignore un overlay plein écran
      let inset = 0;
      for (let y = 1; y <= 260; y += 12) {
        document.elementsFromPoint(x, y).forEach((el) => {
          if (this.contains(el)) return; // ignore la carte finder elle-même
          const pos = getComputedStyle(el).position;
          if (pos !== 'fixed' && pos !== 'sticky') return;
          const bottom = el.getBoundingClientRect().bottom;
          if (bottom > inset && bottom <= maxProbe) inset = bottom;
        });
      }
      return inset;
    }

    /**
     * Décide si la liste s'ouvre vers le haut : uniquement si elle ne tient pas
     * entièrement en dessous ET qu'elle tient réellement au-dessus (hors zone occupée
     * par le header sticky). Si ça ne tient nulle part, on reste en bas (défaut).
     * La liste garde ses dimensions même fermée (visibility:hidden), donc offsetHeight
     * est mesurable avant l'ouverture.
     */
    positionDropdown() {
      const rect = this.dropdownTrigger.getBoundingClientRect();
      const listHeight = this.dropdownList.offsetHeight || 0;
      const margin = 8;
      const needed = listHeight + margin;
      const spaceBelow = window.innerHeight - rect.bottom;
      const spaceAbove = rect.top - this.topInset();
      const dropUp = spaceBelow < needed && spaceAbove >= needed;
      this.dropdown.classList.toggle('is-drop-up', dropUp);
    }

    onDocumentClick(e) {
      if (!this.dropdown.contains(e.target)) this.toggleDropdown(false);
    }

    onListKeydown(e) {
      const options = [...this.dropdownList.querySelectorAll('[role="option"]')];
      const index = options.indexOf(document.activeElement);
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        this.focusOption(Math.min(index + 1, options.length - 1));
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        this.focusOption(Math.max(index - 1, 0));
      } else if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        if (options[index]) this.selectOption(options[index]);
      } else if (e.key === 'Escape') {
        this.toggleDropdown(false);
        this.dropdownTrigger.focus();
      }
    }

    focusOption(i) {
      const options = this.dropdownList.querySelectorAll('[role="option"]');
      if (options[i]) options[i].focus();
    }

    selectOption(option) {
      const value = option.getAttribute('data-value') || '';
      const label = (option.getAttribute('data-label') || option.textContent).trim();

      this.dropdownList.querySelectorAll('[role="option"]').forEach((o) => {
        o.setAttribute('aria-selected', o === option ? 'true' : 'false');
      });
      this.dropdownLabel.textContent = value === '' ? this.allLabel : label;
      this.dropdownInput.value = value;

      this.toggleDropdown(false);
      this.dropdownTrigger.focus();
      // Déclenche la mise à jour du compteur.
      this.dropdownInput.dispatchEvent(new Event('input', { bubbles: true }));
    }

    /* ------------------------------------------------------------------ *
     * Chargement des données de filtres (options catégorie + valeurs taille)
     * en un seul fetch JSON de la collection non filtrée.
     * ------------------------------------------------------------------ */

    async loadFilterData() {
      if (this.dataAbort) this.dataAbort.abort();
      this.dataAbort = new AbortController();

      try {
        const data = await this.fetchFinderData('', this.dataAbort.signal);
        if (!data || !Array.isArray(data.filters)) return;

        // Options du dropdown catégorie.
        if (this.dropdown && this.dropdownInput) {
          const param = this.dropdownInput.getAttribute('name');
          const filter = data.filters.find((f) => f.param_name === param);
          if (filter && Array.isArray(filter.values)) {
            const seen = new Set();
            const options = [];
            filter.values.forEach((v) => {
              const value = String(v.value == null ? '' : v.value).trim();
              if (!value) return;
              const key = value.toLowerCase();
              if (seen.has(key) || this.excludeValues.includes(key)) return;
              seen.add(key);
              options.push({ value: value, label: String(v.label || value).trim() });
            });
            if (options.length) this.renderOptions(options);
          }
        }

        // Valeurs de taille pour le snapping.
        if (this.sizeInput) {
          const sizeParam = this.sizeInput.getAttribute('name');
          const filter = data.filters.find((f) => f.param_name === sizeParam);
          if (filter && Array.isArray(filter.values)) {
            const values = filter.values.map((v) => Number(v.value)).filter((n) => !Number.isNaN(n));
            if (values.length) {
              this.sizeValues = values.sort((a, b) => a - b);
              if (this.showCount) this.updateCount(); // recalcule avec la taille snappée
            }
          }
        }
      } catch (error) {
        if (error.name !== 'AbortError') {
          console.warn('[bike-finder] données de filtres indisponibles :', error.message);
        }
      }
    }

    renderOptions(options) {
      // Conserve la première option (« Toutes catégories »).
      const first = this.dropdownList.querySelector('[role="option"]');
      this.dropdownList.textContent = '';
      if (first) this.dropdownList.appendChild(first);

      options.forEach((opt) => {
        const li = document.createElement('li');
        li.className = 'hpb-finder__dropdown-option';
        li.setAttribute('role', 'option');
        li.setAttribute('tabindex', '-1');
        li.setAttribute('data-value', opt.value);
        li.setAttribute('data-label', opt.label);
        li.setAttribute('aria-selected', 'false');
        li.textContent = opt.label;
        this.dropdownList.appendChild(li);
      });

      if (first) first.setAttribute('tabindex', '-1');
    }

    /* ------------------------------------------------------------------ *
     * Taille : snapping sur la valeur de filtre existante la plus proche.
     * (métachamp liste de valeurs discrètes : Shopify ignore une valeur absente)
     * ------------------------------------------------------------------ */

    snappedSize(raw) {
      const n = parseInt(raw, 10);
      if (Number.isNaN(n)) return null; // valeur non numérique → pas de filtre taille
      if (!this.sizeValues || !this.sizeValues.length) return n; // valeurs pas encore chargées → brut
      return this.sizeValues.reduce((best, v) => (Math.abs(v - n) < Math.abs(best - n) ? v : best));
    }

    snapSizeInput() {
      if (!this.sizeInput) return;
      const snapped = this.snappedSize((this.sizeInput.value || '').trim());
      if (snapped !== null) this.sizeInput.value = snapped;
    }

    /* ------------------------------------------------------------------ *
     * Compteur dynamique
     * ------------------------------------------------------------------ */

    onChange() {
      clearTimeout(this.debounceTimer);
      this.debounceTimer = setTimeout(() => this.updateCount(), 250);
    }

    async updateCount() {
      if (!this.showCount) return;

      const query = this.buildQuery();

      if (this.cache.has(query)) {
        this.renderCount(this.cache.get(query));
        return;
      }

      if (this.countAbort) this.countAbort.abort();
      this.countAbort = new AbortController();
      this.setBusy(true);

      try {
        const count = this.readCount(await this.fetchFinderData(query, this.countAbort.signal));
        if (count !== null) {
          this.cache.set(query, count);
          this.renderCount(count);
        }
      } catch (error) {
        if (error.name !== 'AbortError') {
          console.warn('[bike-finder] compteur indisponible :', error.message);
        }
      } finally {
        this.setBusy(false);
      }
    }

    renderCount(count) {
      if (this.countEl) this.countEl.textContent = count;
    }

    setBusy(isBusy) {
      if (this.submitButton) {
        this.submitButton.setAttribute('aria-busy', isBusy ? 'true' : 'false');
      }
    }
  }

  customElements.define('bike-finder', BikeFinder);
}
